import { Component, ElementRef, ViewChild, AfterViewInit, SimpleChanges, input, output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import QRCode, { QRCodeOptions } from 'qrcode';
import { QRCodeData } from '@zayan-erp/sales';
import { QrCodeService } from '../../services/qr-code.service';

export interface QRData {
  type: 'url' | 'text' | 'email' | 'phone' | 'wifi' | 'vcard' | 'sms';
  content: string;
  label?: string;
}

@Component({
  selector: 'app-qr-generation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './qr-generation.component.html',
  styleUrl: './qr-generation.component.scss'
})
export class QrGenerationComponent implements AfterViewInit {
  @ViewChild('qrCanvas', { static: true }) qrCanvas!: ElementRef<HTMLCanvasElement>;

  // Signal inputs
  data = input<QRCodeData>();
  options = input<QRCodeOptions>({});
  showDownload = input<boolean>(true);
  showCopy = input<boolean>(false);
  label = input<string>('');

  // Signal outputs
  qrGenerated = output<boolean>();
  qrError = output<string>();

  // State
  qrCodeGenerated: boolean = false;
  errorMessage: string = '';
  copySuccess: boolean = false;

  #qrCodeService = inject(QrCodeService);

  // Default options - Separated width from QR options
  private defaultQROptions: QRCodeOptions = {
    errorCorrectionLevel: 'M'
  };

  private defaultWidth = 256;

  ngAfterViewInit() {
    if (this.data()) {
      this.generateQRCode();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.data() && this.qrCanvas) {
      this.generateQRCode();
    }
  }

  // FIXED: Updated to handle width separately from QR options
  private async generateQRCode() {
    try {
      this.errorMessage = '';
      this.qrCodeGenerated = false;

      const canvas = this.qrCanvas.nativeElement;
      const qrData = this.formatQRData(this.data()!);

      let qrOptions: QRCodeOptions = {
        ...this.defaultQROptions,
        ...(this.options().errorCorrectionLevel && { errorCorrectionLevel: this.options().errorCorrectionLevel })
      };

      try {
        await QRCode.toCanvas(canvas, qrData, qrOptions);
        this.qrCodeGenerated = true;
        this.qrGenerated.emit(true);
        return;
      } catch (firstError) {
        qrOptions = {
          ...qrOptions,
          errorCorrectionLevel: 'L' // Lowest error correction for more data capacity
        };

        try {
          await QRCode.toCanvas(canvas, qrData, qrOptions);
          this.qrCodeGenerated = true;
          this.qrGenerated.emit(true);
          return;
        } catch (secondError) {
          const largerWidth = Math.max(this.defaultWidth, 400);
          this.setCanvasSize(canvas, largerWidth);

          qrOptions = {
            ...qrOptions,
            errorCorrectionLevel: 'M'
          };

          try {
            await QRCode.toCanvas(canvas, qrData, qrOptions);
            this.qrCodeGenerated = true;
            this.qrGenerated.emit(true);
            return;
          } catch (thirdError) {
            this.setCanvasSize(canvas, 512);

            const minimalOptions: QRCodeOptions = {
              errorCorrectionLevel: 'L'
            };
            await QRCode.toCanvas(canvas, qrData, minimalOptions);
            this.qrCodeGenerated = true;
            this.qrGenerated.emit(true);
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('version')) {
        this.errorMessage = 'Data is too large for QR code. Please reduce the amount of information.';
      } else if (error instanceof Error && error.message.includes('data')) {
        this.errorMessage = 'Invalid data format. Please check your input.';
      } else {
        this.errorMessage = 'Failed to generate QR code. Please try again.';
      }

      this.qrCodeGenerated = false;
      this.qrError.emit(this.errorMessage);
    }
  }

  // NEW: Helper method to set canvas size
  private setCanvasSize(canvas: HTMLCanvasElement, size: number) {
    canvas.width = size;
    canvas.height = size;
    canvas.style.width = size + 'px';
    canvas.style.height = size + 'px';
  }

  private formatQRData(data: QRCodeData): string {
    switch (data.type) {
      case 'url':
        return this.#qrCodeService.ensureValidUrl(data.content);
      case 'email':
        return this.#qrCodeService.formatEmailData(data.content);
      case 'phone':
        return `tel:${data.content}`;
      case 'sms':
        return this.#qrCodeService.formatSmsData(data.content);
      case 'wifi':
        return this.#qrCodeService.formatWifiData(data.content);
      case 'vcard':
        return this.#qrCodeService.formatVCard(data.content);
      case 'text':
      case 'custom':
      default:
        return data.content.toString();
    }
  }

  downloadQR() {
    if (!this.qrCodeGenerated) return;

    try {
      const canvas = this.qrCanvas.nativeElement;
      const link = document.createElement('a');

      // Generate a meaningful filename based on the QR data
      const filename = this.generateFilename();
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading QR code:', error);
    }
  }

  private generateFilename(): string {
    const data = this.data();
    if (!data) return `qrcode-${Date.now()}.png`;

    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    let baseName = 'qrcode';

    switch (data.type) {
      case 'url':
        try {
          const url = new URL(this.#qrCodeService.ensureValidUrl(data.content));
          baseName = `qr-url-${url.hostname.replace(/[^a-zA-Z0-9]/g, '-')}`;
        } catch {
          baseName = 'qr-url';
        }
        break;
      case 'email':
        if (typeof data.content === 'string') {
          baseName = `qr-email-${data.content.replace(/[^a-zA-Z0-9]/g, '-')}`;
        } else {
          baseName = `qr-email-${data.content.email?.replace(/[^a-zA-Z0-9]/g, '-') || 'contact'}`;
        }
        break;
      case 'phone':
        baseName = `qr-phone-${data.content.replace(/[^0-9]/g, '')}`;
        break;
      case 'wifi':
        if (typeof data.content === 'string') {
          baseName = 'qr-wifi';
        } else {
          baseName = `qr-wifi-${data.content.ssid?.replace(/[^a-zA-Z0-9]/g, '-') || 'network'}`;
        }
        break;
      case 'vcard':
        if (typeof data.content === 'object' && data.content.firstName) {
          baseName = `qr-contact-${data.content.firstName.replace(/[^a-zA-Z0-9]/g, '-')}`;
        } else {
          baseName = 'qr-contact';
        }
        break;
      case 'text':
        baseName = 'qr-text';
        break;
      default:
        baseName = `qr-${data.type}`;
    }

    return `${baseName}-${timestamp}.png`;
  }

  async copyToClipboard() {
    if (!this.qrCodeGenerated) return;

    try {
      const canvas = this.qrCanvas.nativeElement;
      canvas.toBlob(async blob => {
        if (blob) {
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);

          // Show success feedback
          this.copySuccess = true;
          setTimeout(() => {
            this.copySuccess = false;
          }, 2000);
        }
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  }
}
